import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { withRouter } from 'react-router-dom';

import ReceiptIcon from '@material-ui/icons/Receipt';

import Input from '@material-ui/core/Input';

import Paper from '~/components/Paper';
import InputControl from '~/components/Input/InputControl';
import GridContainer from '~/components/GridContainer';
import GridItem from '~/components/GridItem';
import InputSelect from '~/components/Input/InputSelect';
import InputNumber from '~/components/Input/InputNumber';

import Form from '~/containers/Form';
import ProductRecord from '~/records/ProductRecord';
import { packageOptions } from '~/config/constants';
import useStyles from './styles';
import InputCheckBox from '~/components/Input/InputCheckBox';
import Request from '~/providers/Request';

const attrName = (attr) => ProductRecord.hummanAttributeName(attr);

function ProductForm({ record, setRecord, handleChange, onSubmit, loading }) {
  const classesRoot = useStyles();
  const { t } = useTranslation();
  const isPersisted = record.isPersisted();
  const [packOptions, setPackOptions] = useState([]);

  const fetchPacks = () => {
    Request.get('/admin/packs', null, { normalizeJSON: false })
      .then((resp) => {
        const items = resp.data
          .filter((e) => e.attributes)
          .map((e) => ({
            id: Number(e.id),
            name: `${e.attributes.name}`,
          }));
        setPackOptions(items);
      })
      .catch((e) => {
        console.error(e);
      });
  };

  useEffect(() => {
    fetchPacks();
  }, []);

  return (
    <>
      <Form onSubmit={onSubmit}>
        <Paper
          Icon={ReceiptIcon}
          iconColor={isPersisted ? 'purple' : 'blue'}
          title={
            isPersisted
              ? t('products.extras.edit')
              : t('products.extras.create')
          }
          loading={loading}
        >
          <div className={clsx(classesRoot.root)}>
            <GridContainer>
              <GridItem xs={12} sm={4} md={4} lg={4} xl={4}>
                <InputControl
                  name={'name'}
                  value={record.get('name')}
                  label={attrName('name')}
                  required={true}
                  inputComponent={Input}
                  onChange={handleChange}
                />
              </GridItem>
              <GridItem xs={12} sm={4} md={4} lg={4} xl={4}>
                <InputControl
                  name={'description'}
                  value={record.get('description')}
                  label={attrName('description')}
                  required={true}
                  inputComponent={Input}
                  onChange={handleChange}
                />
              </GridItem>
              <GridItem xs={1} sm={1} md={1}>
                <InputControl
                  value={record.get('is_sellable')}
                  name="is_sellable"
                  label={attrName('is_sellable')}
                  inputComponent={InputCheckBox}
                  onChange={handleChange}
                  InputProps={{}}
                />
              </GridItem>
            </GridContainer>

            <GridContainer>
              <GridItem xs={12} sm={4} md={4} lg={4} xl={4}>
                <InputControl
                  name={'pack_type'}
                  label={attrName('pack_type')}
                  value={record.get('pack_type')}
                  required={true}
                  inputComponent={InputSelect}
                  onChange={({ pack_type }) => {
                    setRecord(
                      record.updateAttributes({ pack_type: pack_type })
                    );
                  }}
                  InputProps={{
                    isClearable: true,
                    outValueType: 'string',
                    options: Object.keys(packageOptions).map((key) => ({
                      id: key,
                      name: packageOptions[key],
                    })),
                  }}
                />
              </GridItem>

              {record.get('pack_type') === 'pack' && (
                <GridItem xs={12} sm={4} md={4} lg={4} xl={4}>
                  <InputControl
                    name={'pack_id'}
                    label={attrName('pack')}
                    value={record.get('pack_id')}
                    required={true}
                    inputComponent={InputSelect}
                    onChange={({ pack_id }) => {
                      setRecord(record.updateAttributes({ pack_id }));
                    }}
                    InputProps={{
                      isClearable: true,
                      outValueType: 'number',
                      options: packOptions,
                    }}
                  />
                </GridItem>
              )}
            </GridContainer>

            <GridContainer>
              <GridItem xs={12} sm={4} md={4} lg={4} xl={4}>
                <InputControl
                  name={'ean'}
                  value={record.get('ean')}
                  label={attrName('ean')}
                  required={true}
                  inputComponent={Input}
                  onChange={handleChange}
                />
              </GridItem>

              <GridItem xs={12} sm={4} md={4} lg={4} xl={4}>
                <InputControl
                  name={'bin'}
                  value={record.get('bin')}
                  label={attrName('bin')}
                  inputComponent={Input}
                  onChange={handleChange}
                />
              </GridItem>

              <GridItem xs={12} sm={5} md={4} lg={3} xl={2}>
                <InputControl
                  value={record.get('has_membership_fee')}
                  name="has_membership_fee"
                  label={attrName('has_membership_fee')}
                  inputComponent={InputCheckBox}
                  onChange={handleChange}
                  InputProps={{}}
                />
              </GridItem>

              <GridItem xs={12} sm={6} md={4} lg={3} xl={2}>
                <InputControl
                  name={'membership_amount'}
                  value={record.get('membership_amount')}
                  label={attrName('membership_amount')}
                  inputComponent={InputNumber}
                  onChange={handleChange}
                  disabled={!record.get('has_membership_fee')}
                  required={record.get('has_membership_fee')}
                  InputProps={{
                    precision: 0,
                    showMask: false,
                  }}
                />
              </GridItem>
            </GridContainer>

            <GridContainer>
              <GridItem xs={12} sm={3} md={3} lg={3} xl={3}>
                <InputControl
                  name={'amount'}
                  value={record.get('amount')}
                  label={attrName('amount')}
                  required={true}
                  inputComponent={InputNumber}
                  onChange={handleChange}
                  InputProps={{
                    precision: 0,
                    showMask: false,
                  }}
                />
              </GridItem>

              <GridItem xs={12} sm={3} md={3} lg={3} xl={3}>
                <InputControl
                  name={'currency'}
                  value={record.get('currency')}
                  label={attrName('currency')}
                  required={true}
                  inputComponent={InputNumber}
                  onChange={handleChange}
                  InputProps={{
                    precision: 0,
                    showMask: false,
                  }}
                />
              </GridItem>

              <GridItem sm={2} md={2} lg={2} xl={2}>
                <InputControl
                  name={'quantity'}
                  value={record.get('quantity')}
                  label={attrName('quantity')}
                  inputComponent={InputNumber}
                  onChange={handleChange}
                  InputProps={{
                    precision: 0,
                    showMask: false,
                  }}
                />
              </GridItem>

              <GridItem xs={1} sm={1} md={1}>
                <InputControl
                  value={record.get('unlimited')}
                  name="unlimited"
                  label={attrName('unlimited')}
                  inputComponent={InputCheckBox}
                  onChange={handleChange}
                  InputProps={{}}
                />
              </GridItem>

              <GridItem sm={3} md={3} lg={3} xl={3}>
                <InputControl
                  name={'duration'}
                  value={record.get('duration')}
                  label={attrName('duration')}
                  required={true}
                  inputComponent={InputNumber}
                  onChange={handleChange}
                  InputProps={{
                    precision: 0,
                    showMask: false,
                  }}
                />
              </GridItem>
            </GridContainer>
          </div>
        </Paper>
      </Form>
    </>
  );
}

ProductForm.propTypes = {
  record: PropTypes.instanceOf(ProductRecord).isRequired,
  handleChange: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  loading: PropTypes.bool,
  setRecord: PropTypes.func.isRequired,
};

ProductForm.defaultProps = {
  loading: false,
};

export default React.memo(withRouter(ProductForm));
